import { isMockMode } from '../config/amplify'

export { isMockMode }

export const MOCK_ROLE_KEY = 'unwritten80_mock_role'
export const MOCK_SESSION_KEY = 'unwritten80_mock_session'

const MOCK_USERS = {
  student: {
    sub: 'mock-student-sub',
    email: 'student@example.com',
    name: 'Demo Student',
    picture: null,
    status: 'approved',
    isAdmin: false,
  },
  pending: {
    sub: 'mock-pending-sub',
    email: 'pending@example.com',
    name: 'Pending Student',
    picture: null,
    status: 'pending',
    isAdmin: false,
  },
  removed: {
    sub: 'mock-removed-sub',
    email: 'removed@example.com',
    name: 'Removed Student',
    picture: null,
    status: 'removed',
    isAdmin: false,
  },
  admin: {
    sub: 'mock-admin-sub',
    email: 'admin@example.com',
    name: 'Facilitator Admin',
    picture: null,
    status: 'approved',
    isAdmin: true,
  },
}

export function getMockRole() {
  return localStorage.getItem(MOCK_ROLE_KEY) || 'student'
}

export function setMockRole(role) {
  localStorage.setItem(MOCK_ROLE_KEY, role)
  localStorage.setItem(MOCK_SESSION_KEY, '1')
}

export function clearMockSession() {
  localStorage.removeItem(MOCK_SESSION_KEY)
  localStorage.removeItem(MOCK_ROLE_KEY)
}

function getMockUser() {
  if (!localStorage.getItem(MOCK_SESSION_KEY)) {
    return null
  }
  const role = getMockRole()
  return MOCK_USERS[role] || MOCK_USERS.student
}

/**
 * Returns session-like info or null if not signed in.
 * { sub, email, name, picture, tokens?: { idToken } }
 */
export async function getSession() {
  if (isMockMode()) {
    const user = getMockUser()
    if (!user) return null
    return {
      sub: user.sub,
      email: user.email,
      name: user.name,
      picture: user.picture,
      isAdmin: user.isAdmin,
      status: user.status,
      tokens: { idToken: `mock-token-${user.sub}` },
    }
  }

  const { fetchAuthSession, getCurrentUser } = await import('aws-amplify/auth')
  try {
    const user = await getCurrentUser()
    const session = await fetchAuthSession()
    const idToken = session.tokens?.idToken
    const payload = idToken?.payload || {}
    const groups = payload['cognito:groups'] || []

    return {
      sub: user.userId,
      email: payload.email,
      name: payload.name || payload.email,
      picture: payload.picture || null,
      isAdmin: Array.isArray(groups) && groups.includes('admin'),
      tokens: {
        idToken: idToken?.toString() || null,
      },
    }
  } catch {
    return null
  }
}

export async function getIdToken() {
  const session = await getSession()
  return session?.tokens?.idToken || null
}

export async function signInWithGoogle() {
  if (isMockMode()) {
    throw new Error('Use mock role buttons in Login when running locally')
  }
  const { signInWithRedirect } = await import('aws-amplify/auth')
  await signInWithRedirect({ provider: 'Google' })
}

/** Mock-only: establish a local session with the given role. */
export async function mockSignIn(role = 'student') {
  if (!isMockMode()) {
    throw new Error('mockSignIn is only available in local mock mode')
  }
  setMockRole(role)
  return getSession()
}

export async function signOut() {
  if (isMockMode()) {
    clearMockSession()
    return
  }
  const { signOut: amplifySignOut } = await import('aws-amplify/auth')
  await amplifySignOut()
}

/**
 * Sign-out for UI buttons: clears the session and leaves the app in one click.
 *
 * Hosted-UI sessions have to be ended by Cognito, not by us. Amplify's signOut()
 * clears the local tokens and then sets window.location to
 * `https://<authDomain>/logout?...&logout_uri=<signout dest>`, which drops the
 * Cognito cookie and returns to that destination. Assigning window.location here
 * would overwrite that pending navigation, so the Cognito session would survive
 * and the next Google sign-in would silently restore the same user.
 */
export async function signOutAndLeave() {
  const dest = import.meta.env.VITE_REDIRECT_SIGNOUT || 'https://unwritten80.com'

  if (isMockMode()) {
    clearMockSession()
    // Local dev only: `dest` can be another copy of this app on a different port,
    // which keeps its own signed-in session, so go to our own login page instead.
    window.location.href = '/login'
    return
  }

  const { signOut: amplifySignOut } = await import('aws-amplify/auth')
  try {
    await amplifySignOut()
  } catch {
    window.location.href = dest
    return
  }
  // Sessions that did not come from the hosted UI get no redirect from Amplify;
  // leave on our own if its logout navigation has not taken over by now.
  setTimeout(() => {
    window.location.href = dest
  }, 1500)
}
